import React, { useState, useEffect, useRef } from "react";
import useAuth from "../../Auth/useAuth";
import axios from "axios";
import "./ReviseBill.css";
import AlertModal from "../AlertModal/AlertModal";
import { useNavigate, useLocation } from "react-router-dom";
import config from "../../config";
import GenerateRevisedBillModal from "./GenerateRevisedBillModal";
import BillingIdConfirmModal from "./BillingIdConfirmModal";

const listReviseBillUrl = `${config.BASE_URL}/list-to-revise-bill`;
const downloadPdfUrl = `${config.BASE_URL}/generate-pdf-revised-bill`;
const updateOneReviseBill = `${config.BASE_URL}/update-one-revise-bill`;

const ReviseBill = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  const {
    setModuleChange,
    setTripIDState,
    setActiveModule,
    setSearchValue,
    reviseBillState,
    setReviseBillState,
  } = useAuth();

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    bill_id: "",
  });
  const [tableData, setTableData] = useState({ billed: [], acknowledged: [] });
  const [noDataMessage, setNoDataMessage] = useState("");
  const [selectedBilled, setSelectedBilled] = useState([]);
  const [selectedAcknowledged, setSelectedAcknowledged] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showBillModal, setShowBillModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const selectAllBilledRef = useRef(null);
  const selectAllAckRef = useRef(null);

  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editingType, setEditingType] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [billingIdConfirmModal, setBillingIdConfirmModal] = useState({
    isOpen: false,
    maxBillingId: "",
    otherBillingIds: [],
  });

  // Add modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  // Modal handler functions
  const showModal = (title, message, type = "info") => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type,
    });
  };

  const closeModal = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
  };

  // Fetch table data function
  const fetchTableData = async (filterParams) => {
    try {
      setIsLoading(true);
      const cleanFilters = {};

      if (filterParams.bill_id) cleanFilters.bill_id = filterParams.bill_id;
      if (filterParams.startDate)
        cleanFilters.startDate = filterParams.startDate;
      if (filterParams.endDate) cleanFilters.endDate = filterParams.endDate;

      const response = await axios.post(listReviseBillUrl, cleanFilters, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      console.log("List Bills [billed, acknowledged]:", response.data);
      if (!response?.data?.billed && !response?.data?.acknowledged) {
        showModal("Error", response.data.message, "error");
      }

      const resultData = {
        billed: response?.data?.billed || [],
        acknowledged: response?.data?.acknowledged || [],
      };

      setTableData(resultData);
      setHasSearched(true);

      if (
        resultData.billed.length === 0 &&
        resultData.acknowledged.length === 0
      ) {
        setNoDataMessage(response.data.message || "No records available");
      } else {
        setNoDataMessage("");
      }
    } catch (error) {
      setTableData({ billed: [], acknowledged: [] });
      setNoDataMessage(error.response?.data?.message || "Error fetching data");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle initial mount and URL parameter changes
  useEffect(() => {
    sessionStorage.setItem("actMod", "revise-bill");
    setActiveModule("revise-bill");

    const searchParams = new URLSearchParams(location.search);
    const billIdFromUrl = searchParams.get("bill_id");

    // Check if coming from navbar search
    const fromNavbarSearch = sessionStorage.getItem(
      "reviseBillSearchProcessed",
    );

    if (billIdFromUrl) {
      setFilters((prev) => ({
        ...prev,
        bill_id: billIdFromUrl,
      }));

      // If data is available from navbar search, use it
      if (fromNavbarSearch === "true" && reviseBillState) {
        const resultData = {
          billed: reviseBillState.billed || [],
          acknowledged: reviseBillState.acknowledged || [],
        };
        setTableData(resultData);
        setHasSearched(true);

        if (
          resultData.billed.length === 0 &&
          resultData.acknowledged.length === 0
        ) {
          setNoDataMessage("No records available");
        } else {
          setNoDataMessage("");
        }

        sessionStorage.removeItem("reviseBillSearchProcessed");
      } else {
        // Otherwise fetch the data
        fetchTableData({ bill_id: billIdFromUrl });
      }
    } else {
      // Reset state if no bill_id in URL
      resetComponentState();
    }
  }, [location.pathname, location.search]);

  // Reset function
  const resetComponentState = () => {
    setFilters({
      startDate: "",
      endDate: "",
      bill_id: "",
    });
    setTableData({ billed: [], acknowledged: [] });
    setNoDataMessage("");
    setSelectedBilled([]);
    setSelectedAcknowledged([]);
    setHasSearched(false);
    setIsLoading(false);
  };

  // Handle checkbox indeterminate state
  useEffect(() => {
    if (selectAllBilledRef.current) {
      const isIndeterminate =
        selectedBilled.length > 0 &&
        selectedBilled.length < tableData.billed.length;
      selectAllBilledRef.current.indeterminate = isIndeterminate;
    }

    if (selectAllAckRef.current) {
      const isIndeterminate =
        selectedAcknowledged.length > 0 &&
        selectedAcknowledged.length < tableData.acknowledged.length;
      selectAllAckRef.current.indeterminate = isIndeterminate;
    }
  }, [selectedBilled, selectedAcknowledged, tableData]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (name === "bill_id") {
      if (value === "") {
        setFilters({
          ...filters,
          bill_id: "",
        });
        return;
      }

      let formattedValue = value.toUpperCase().trim();
      if (!formattedValue.startsWith("B")) {
        formattedValue = "B" + formattedValue;
      }

      const numbersOnly = formattedValue.substring(1).replace(/\D/g, "");
      formattedValue = "B" + numbersOnly;

      setFilters({
        ...filters,
        bill_id: formattedValue,
      });
    } else {
      setFilters({
        ...filters,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (filters.startDate && filters.endDate) {
      if (new Date(filters.startDate) > new Date(filters.endDate)) {
        showModal(
          "Error",
          "End date should be greater than or equal to Start Date.",
          "error",
        );
        return;
      }
    }

    if (
      ((filters.startDate && !filters.endDate) ||
        (!filters.startDate && filters.endDate)) &&
      !filters.bill_id
    ) {
      showModal("Error", "Please select both start-date and end-date", "error");
      return;
    }

    let effectiveFilters = { ...filters };

    // If no filters, use current data's bill_id
    if (
      !effectiveFilters.startDate &&
      !effectiveFilters.endDate &&
      !effectiveFilters.bill_id
    ) {
      if (editingData?.billing_id) {
        effectiveFilters.bill_id = editingData.billing_id;
      } else if (
        tableData.billed.length > 0 &&
        tableData.billed[0].billing_id
      ) {
        effectiveFilters.bill_id = tableData.billed[0].billing_id;
      } else if (
        tableData.acknowledged.length > 0 &&
        tableData.acknowledged[0].billing_id
      ) {
        effectiveFilters.bill_id = tableData.acknowledged[0].billing_id;
      }
    }

    await fetchTableData(effectiveFilters);
  };

  const handleCheckboxChange = (id, isBilled) => {
    if (isBilled) {
      setSelectedBilled((prev) =>
        prev.includes(id) ? prev.filter((_id) => _id !== id) : [...prev, id],
      );
    } else {
      setSelectedAcknowledged((prev) =>
        prev.includes(id) ? prev.filter((_id) => _id !== id) : [...prev, id],
      );
    }
  };

  const handleSelectAllBilled = (checked) => {
    if (checked) {
      setSelectedBilled(tableData.billed.map((item) => item._id));
    } else {
      setSelectedBilled([]);
    }
  };

  const handleSelectAllAck = (checked) => {
    if (checked) {
      setSelectedAcknowledged(tableData.acknowledged.map((item) => item._id));
    } else {
      setSelectedAcknowledged([]);
    }
  };

  const handleGenerateBillClick = () => {
    if (selectedBilled.length === 0 && selectedAcknowledged.length === 0) {
      showModal(
        "No Selection",
        "Please select at least one row to generate the bill.",
        "warning",
      );
      return;
    }

    // Get selected rows
    const billedSelectedRows = tableData.billed.filter((row) =>
      selectedBilled.includes(row._id),
    );
    const ackSelectedRows = tableData.acknowledged.filter((row) =>
      selectedAcknowledged.includes(row._id),
    );

    const allSelectedRows = [...billedSelectedRows, ...ackSelectedRows];

    const uniqueVendors = new Set();
    allSelectedRows.forEach((row) => {
      if (row.vendor) uniqueVendors.add(row.vendor);
    });

    if (uniqueVendors.size > 1) {
      showModal(
        "Multiple Vendors Selected",
        "Please select trips from only one vendor to generate the bill.",
        "warning",
      );
      // Clear selections to prevent user confusion
      setSelectedBilled([]);
      setSelectedAcknowledged([]);
      return;
    }

    // Check for multiple billing IDs
    const billingIds = new Set();

    allSelectedRows.forEach((row) => {
      // Clean and validate billing ID
      if (row.billing_id) {
        const cleanedId = row.billing_id.toString().trim();

        // Skip if empty string, "0", "null", "undefined", or any falsy-looking value
        if (
          cleanedId &&
          cleanedId !== "0" &&
          cleanedId !== "null" &&
          cleanedId !== "undefined" &&
          cleanedId.length > 0
        ) {
          billingIds.add(cleanedId);
        }
      }
    });

    console.log("Found billing IDs:", Array.from(billingIds)); // Debug log

    // Show modal ONLY if we have multiple DIFFERENT valid billing IDs
    if (billingIds.size > 1) {
      const billingIdArray = Array.from(billingIds);

      // Find the maximum billing ID - handle various formats
      const maxBillingId = billingIdArray.reduce((max, current) => {
        // Extract numbers from strings like "B123", "BILL-456", "INV789", etc.
        const maxMatch = max.match(/\d+/);
        const currentMatch = current.match(/\d+/);

        const maxNum = maxMatch ? parseInt(maxMatch[0]) : 0;
        const currentNum = currentMatch ? parseInt(currentMatch[0]) : 0;

        return currentNum > maxNum ? current : max;
      });

      // Get other billing IDs
      const otherBillingIds = billingIdArray.filter(
        (id) => id !== maxBillingId,
      );

      // Show confirmation modal
      setBillingIdConfirmModal({
        isOpen: true,
        maxBillingId,
        otherBillingIds,
      });
      return;
    }
    
    setShowBillModal(true);
  };

  const handleGenerateBill = async (additionalRemarks) => {
    // Double-check for empty selection (safety check)
    if (selectedBilled.length === 0 && selectedAcknowledged.length === 0) {
      showModal(
        "No Selection",
        "Please select at least one row to generate the bill.",
        "warning",
      );
      setShowBillModal(false);
      return;
    }

    const billedSelectedRows = tableData.billed.filter((row) =>
      selectedBilled.includes(row._id),
    );
    const billedUnselectedRows = tableData.billed.filter(
      (row) => !selectedBilled.includes(row._id),
    );
    const ackSelectedRows = tableData.acknowledged.filter((row) =>
      selectedAcknowledged.includes(row._id),
    );

    // Get billing ID
    let currentBillId = "";

    // Use confirmed billing ID from modal if available
    if (billingIdConfirmModal.maxBillingId) {
      currentBillId = billingIdConfirmModal.maxBillingId;
    } else if (
      billedSelectedRows.length > 0 &&
      billedSelectedRows[0].billing_id
    ) {
      currentBillId = billedSelectedRows[0].billing_id;
    } else if (ackSelectedRows.length > 0 && ackSelectedRows[0].billing_id) {
      currentBillId = ackSelectedRows[0].billing_id;
    }

    const payload = {
      billedSelectedRows,
      billedUnselectedRows,
      ackSelectedRows,
      additionalRemarks,
    };

    setIsDownloading(true);
    try {
      const response = await axios.post(downloadPdfUrl, payload, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
        responseType: "blob",
      });

      if (response.status !== 200) {
        showModal(
          "Error",
          "Failed to generate PDF. Please try again later.",
          "error",
        );
        return;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `RevisedBill-${currentBillId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showModal("Success", "Revised bill generated successfully.", "success");

      setIsRefreshing(true);
      const refetchFilters = { bill_id: currentBillId };
      await fetchTableData(refetchFilters);
      setIsRefreshing(false);

      setSelectedBilled([]);
      setSelectedAcknowledged([]);

      // Clean up modal state
      if (billingIdConfirmModal.isOpen) {
        setBillingIdConfirmModal({
          isOpen: false,
          maxBillingId: "",
          otherBillingIds: [],
        });
      }
    } catch (error) {
      console.error("Error generating revised bill:", error);
      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorData = JSON.parse(await error.response.data.text());
          showModal(
            "Error",
            errorData.message || "Failed to generate revised bill",
            "error",
          );
        } catch (parseError) {
          showModal(
            "Error",
            "Failed to generate revised bill. Please try again.",
            "error",
          );
        }
      } else {
        showModal(
          "Error",
          "Failed to generate revised bill. Please try again.",
          "error",
        );
      }
    } finally {
      setIsDownloading(false);
      setShowBillModal(false);
      setSelectedBilled([]);
      setSelectedAcknowledged([]);
    }
  };

  // Modal handlers remain the same
  const handleConfirm = () => {
    setBillingIdConfirmModal({
      isOpen: false,
      maxBillingId: "",
      otherBillingIds: [],
    });
    setShowBillModal(true);
  };

  const handleCancel = () => {
    setBillingIdConfirmModal({
      isOpen: false,
      maxBillingId: "",
      otherBillingIds: [],
    });
    setSelectedBilled([]);
    setSelectedAcknowledged([]);
  };

  // Click outside handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (!editMode) return;

      const isEditButton = event.target.closest(".edit-btn");
      const isEditFormElement = event.target.closest(".edit-form-container");
      const isEditControl = event.target.closest(".edit-controls");
      const isInput =
        event.target.tagName === "INPUT" || event.target.tagName === "SELECT";
      const isSaveButton = event.target.closest(".save-btn");

      if (
        !isEditButton &&
        !isEditFormElement &&
        !isEditControl &&
        !isInput &&
        !isSaveButton
      ) {
        setEditingId(null);
        setEditingData({});
        setEditMode(false);
        setEditingType(null);
      }
    }

    document.body.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.body.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editMode]);

  const handleEdit = (id, type) => {
    if (editMode && editingId !== id) {
      setEditingId(null);
      setEditingData({});
    }

    const data = type === "billed" ? tableData.billed : tableData.acknowledged;
    const rowData = data.find((item) => item._id === id);

    if (rowData) {
      setEditingId(id);
      setEditingData({ ...rowData });
      setEditMode(true);
      setEditingType(type);

      setTimeout(() => {
        const targetTable =
          type === "billed"
            ? document.querySelector(".billed-table")
            : document.querySelector(".acknowledged-table");

        if (targetTable) {
          const editRow =
            targetTable.querySelector(`tr[data-id="${id}"]`) ||
            Array.from(targetTable.querySelectorAll("tr")).find((tr) =>
              tr.textContent.includes(id.toString()),
            );

          if (editRow) {
            editRow.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      }, 100);
    }
  };

  const handleEditingChange = (e) => {
    const { name, value } = e.target;
    setEditingData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async (id) => {
    try {
      const currentBillId = editingData.billing_id;

      setTableData((prev) => {
        const updatedData = { ...prev };
        if (editingType === "billed") {
          updatedData.billed = prev.billed.map((item) =>
            item._id === id ? { ...editingData } : item,
          );
        } else if (editingType === "acknowledged") {
          updatedData.acknowledged = prev.acknowledged.map((item) =>
            item._id === id ? { ...editingData } : item,
          );
        }
        return updatedData;
      });

      const payload = {
        updateRows: editingData,
        trip_id: editingData.trip_id,
        bill_id: editingData.billing_id,
      };

      const response = await axios.post(updateOneReviseBill, payload, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 201) {
        const refetchFilters = { bill_id: currentBillId };
        if (filters.startDate && filters.endDate) {
          refetchFilters.startDate = filters.startDate;
          refetchFilters.endDate = filters.endDate;
        }

        await fetchTableData(refetchFilters);
        showModal(
          "Success",
          response.data.message || "Update successful",
          "success",
        );
      }
    } catch (error) {
      console.error("Update failed:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update. Reverting changes...";
      showModal("Error", errorMessage, "error");

      try {
        const refetchFilters = { bill_id: editingData.billing_id };
        if (filters.startDate && filters.endDate) {
          refetchFilters.startDate = filters.startDate;
          refetchFilters.endDate = filters.endDate;
        }
        await fetchTableData(refetchFilters);
      } catch (refetchError) {
        console.error("Refetch failed:", refetchError);
      }
    } finally {
      setEditingId(null);
      setEditingData({});
      setEditMode(false);
      setEditingType(null);
    }
  };

  const handleKeyDown = (e, id) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave(id);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setEditingId(null);
      setEditingData({});
      setEditMode(false);
      setEditingType(null);
    }
  };

  return (
    <div className="generate-revise-bill">
      <AlertModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
      />
      {/* Billing ID Confirmation Modal */}
      <BillingIdConfirmModal
        isOpen={billingIdConfirmModal.isOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        maxBillingId={billingIdConfirmModal.maxBillingId}
        otherBillingIds={billingIdConfirmModal.otherBillingIds}
      />
      <div className="filter-box">
        <form onSubmit={handleSubmit}>
          <div className="filters-grid">
            <div className="filter-item">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-item">
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-item">
              <label>Bill Id</label>
              <input
                type="text"
                name="bill_id"
                value={filters.bill_id}
                onChange={handleFilterChange}
                placeholder="Search by bill id: B20"
                pattern="B\d+"
                title="Bill ID must start with B followed by numbers (e.g. B20)"
              />
            </div>
          </div>

          <div className="button-group">
            <button type="submit" className="search-btn">
              Search
            </button>
            {(tableData.billed.length > 0 ||
              tableData.acknowledged.length > 0) && (
              <button
                type="button"
                className="download-btn"
                onClick={handleGenerateBillClick}
                disabled={
                  isDownloading ||
                  (selectedBilled.length === 0 &&
                    selectedAcknowledged.length === 0)
                }
              >
                {isDownloading ? "Downloading..." : "Update Bill"}
              </button>
            )}
          </div>
        </form>
      </div>

      {showBillModal && (
        <GenerateRevisedBillModal
          onClose={() => setShowBillModal(false)}
          onGenerate={handleGenerateBill}
        />
      )}

      {isLoading && (
        <div className="modal-spinner-overlay">
          <div className="spinner"></div>
          <span>Loading data...</span>
        </div>
      )}

      {!isLoading && (hasSearched || noDataMessage) && (
        <div className="tables-container">
          {(tableData.billed.length > 0 || noDataMessage) && (
            <div className="table-section">
              <div className="table-header-container">
                <h2 className="table-title">Billed Trips</h2>
                {filters.bill_id && tableData.billed.length > 0 && (
                  <div className="table-info-container">
                    <h5 className="info-item">
                      Remarks: {tableData.billed[0].remarks_amount}
                    </h5>
                    <hr
                      style={{ width: "100%", borderTop: "0.12rem solid #ddd" }}
                    />
                    <h4 className="info-item">
                      Total Amount:{" "}
                      {
                        tableData.billed[tableData.billed.length - 1]
                          .final_amount
                      }
                    </h4>
                  </div>
                )}
              </div>
              <div className="table-box">
                <div className="table-responsive">
                  <table className="billed-table">
                    <thead>
                      <tr>
                        <th>
                          {tableData.billed.length > 0 && (
                            <div className="select-all-container">
                              {selectedBilled.length > 0 ? (
                                <>
                                  <input
                                    type="checkbox"
                                    ref={selectAllBilledRef}
                                    checked={
                                      selectedBilled.length ===
                                      tableData.billed.length
                                    }
                                    onChange={(e) =>
                                      handleSelectAllBilled(e.target.checked)
                                    }
                                  />
                                  <span> Select All</span>
                                </>
                              ) : (
                                <span>Select</span>
                              )}
                            </div>
                          )}
                        </th>
                        <th>Trip ID</th>
                        <th>Username</th>
                        <th>Profile</th>
                        <th>Truck Number</th>
                        <th>Date</th>
                        <th>Vendor</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Wt.</th>
                        <th>A. Wt.</th>
                        <th>Freight</th>
                        <th>Rate</th>
                        <th>Diesel</th>
                        <th>Adv.</th>
                        <th>Toll</th>
                        <th>TDS</th>
                        <th>Short</th>
                        <th>GST Amount</th>
                        <th>Transaction</th>
                        <th>Amount</th>
                        <th>Bill ID</th>
                        <th>Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.billed.length === 0 ? (
                        <tr>
                          <td
                            colSpan="23"
                            style={{ textAlign: "center", padding: "20px" }}
                          >
                            No billed trips found
                          </td>
                        </tr>
                      ) : (
                        tableData.billed.map((row) => (
                          <tr key={`billed-${row._id}`} data-id={row._id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedBilled.includes(row._id)}
                                onChange={() =>
                                  handleCheckboxChange(row._id, true)
                                }
                              />
                            </td>
                            <td
                              onClick={async () => {
                                setModuleChange("/home");
                                const response = await axios.get(
                                  `/api/trip/${row.trip_id}`,
                                  {
                                    withCredentials: true,
                                    headers: {
                                      "Content-Type": "application/json",
                                    },
                                  },
                                );
                                setTripIDState(response.data.TripData);
                                Navigate(`/home/${row.trip_id}`);
                              }}
                              className="trip-id"
                            >
                              {row.trip_id || null}
                            </td>
                            <td>{row.username}</td>
                            <td>{row.profile}</td>
                            <td>{row.truck_no}</td>
                            <td>{row.date}</td>
                            <td>{row.vendor}</td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="text"
                                  name="destination_from"
                                  value={editingData.destination_from || ""}
                                  onChange={handleEditingChange}
                                  onKeyDown={(e) => handleKeyDown(e, row._id)}
                                />
                              ) : (
                                row.destination_from
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="text"
                                  name="destination_to"
                                  value={editingData.destination_to || ""}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.destination_to
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="weight"
                                  value={editingData.weight}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.weight
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="actual_weight"
                                  value={editingData.actual_weight}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.actual_weight
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="freight"
                                  value={editingData.freight}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.freight
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="rate"
                                  value={editingData.rate}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.rate
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="diesel"
                                  value={editingData.diesel}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.diesel
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="advance"
                                  value={editingData.advance}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.advance
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="toll"
                                  value={editingData.toll}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.toll
                              )}
                            </td>
                            <td>{row.tds}</td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="short"
                                  value={editingData.short}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.short
                              )}
                            </td>
                            <td>{row.gst_amount}</td>
                            <td>{row.transaction_status}</td>
                            <td>{row.amount}</td>
                            <td>{row.billing_id}</td>
                            <td>
                              {editingId === row._id ? (
                                <button
                                  className="save-btn"
                                  onClick={() => handleSave(row._id)}
                                >
                                  Save
                                </button>
                              ) : (
                                <button
                                  className="edit-btn"
                                  onClick={() => handleEdit(row._id, "billed")}
                                >
                                  Edit
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {(tableData.acknowledged.length > 0 || noDataMessage) && (
            <div className="table-section">
              <h2 className="table-title">Acknowledged Trips</h2>
              <div className="table-box">
                <div className="table-responsive">
                  <table className="acknowledged-table">
                    <thead>
                      <tr>
                        <th>
                          {tableData.acknowledged.length > 0 && (
                            <div className="select-all-container">
                              {selectedAcknowledged.length > 0 ? (
                                <>
                                  <input
                                    type="checkbox"
                                    ref={selectAllAckRef}
                                    checked={
                                      selectedAcknowledged.length ===
                                      tableData.acknowledged.length
                                    }
                                    onChange={(e) =>
                                      handleSelectAllAck(e.target.checked)
                                    }
                                  />
                                  <span> Select All</span>
                                </>
                              ) : (
                                <span>Select</span>
                              )}
                            </div>
                          )}
                        </th>
                        <th>Trip ID</th>
                        <th>Username</th>
                        <th>Profile</th>
                        <th>Truck Number</th>
                        <th>Date</th>
                        <th>Vendor</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Weight</th>
                        <th>Actual Weight</th>
                        <th>Freight</th>
                        <th>Rate</th>
                        <th>Diesel</th>
                        <th>Advance</th>
                        <th>Toll</th>
                        <th>TDS Amount</th>
                        <th>Short</th>
                        <th>T.Status</th>
                        <th>Amount</th>
                        <th>Bill ID</th>
                        <th>Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.acknowledged.length === 0 ? (
                        <tr>
                          <td
                            colSpan="22"
                            style={{ textAlign: "center", padding: "20px" }}
                          >
                            No acknowledged trips found
                          </td>
                        </tr>
                      ) : (
                        tableData.acknowledged.map((row) => (
                          <tr key={`acknowledged-${row._id}`} data-id={row._id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedAcknowledged.includes(row._id)}
                                onChange={() =>
                                  handleCheckboxChange(row._id, false)
                                }
                              />
                            </td>
                            <td
                              onClick={async () => {
                                setModuleChange("/home");
                                const response = await axios.get(
                                  `/api/trip/${row.trip_id}`,
                                  {
                                    withCredentials: true,
                                    headers: {
                                      "Content-Type": "application/json",
                                    },
                                  },
                                );
                                setTripIDState(response.data.TripData);
                                Navigate(`/home/${row.trip_id}`);
                              }}
                              className="trip-id"
                            >
                              {row.trip_id || null}
                            </td>
                            <td>{row.username}</td>
                            <td>{row.profile}</td>
                            <td>{row.truck_no}</td>
                            <td>{row.date}</td>
                            <td>{row.vendor}</td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="text"
                                  name="destination_from"
                                  value={editingData.destination_from || ""}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.destination_from
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="text"
                                  name="destination_to"
                                  value={editingData.destination_to || ""}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.destination_to
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="weight"
                                  value={editingData.weight}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.weight
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="actual_weight"
                                  value={editingData.actual_weight}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.actual_weight
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="freight"
                                  value={editingData.freight}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.freight
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="rate"
                                  value={editingData.rate}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.rate
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="diesel"
                                  value={editingData.diesel}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.diesel
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="advance"
                                  value={editingData.advance}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.advance
                              )}
                            </td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="toll"
                                  value={editingData.toll}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.toll
                              )}
                            </td>
                            <td>{row.tds}</td>
                            <td>
                              {editingId === row._id ? (
                                <input
                                  type="number"
                                  name="short"
                                  value={editingData.short}
                                  onChange={handleEditingChange}
                                />
                              ) : (
                                row.short
                              )}
                            </td>
                            <td>{row.transaction_status}</td>
                            <td>{row.amount}</td>
                            <td>{row.billing_id}</td>
                            <td>
                              {editingId === row._id ? (
                                <button
                                  className="save-btn"
                                  onClick={() => handleSave(row._id)}
                                >
                                  Save
                                </button>
                              ) : (
                                <button
                                  className="edit-btn"
                                  onClick={() =>
                                    handleEdit(row._id, "acknowledged")
                                  }
                                >
                                  Edit
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviseBill;
